import { useLocation, useNavigate } from 'react-router-dom';
import { queryStringToFilterValues, toApiSearchCommand } from '../fdjUsersUtils';
import { useCallback, useMemo } from 'react';
import { FdjUsersFilterValues, FdjUsersSortableFields } from '../fdjUsersTypes';
import { useDataLoader } from '@eogile/agrements-common/src/hooks/useDataLoader';
import { PaginationAndSortCommand } from '@eogile/agrements-common/src/components/table/tableTypes';
import { toQueryString } from '@eogile/agrements-common/src/utils/queryStringUtils';
import { mergePaginationAndSortCommandWithParams } from '@eogile/agrements-common/src/utils/tableUtils';
import { PaginatedListResponse } from '@eogile/agrements-common/src/api/apiTypes';
import { FdjUser } from '../../fdjUserTypes';
import ApiHelper from '@eogile/agrements-common/src/api/apiHelper';

/**
 * Hook that offers utilities for the users list:
 *   - Transform the query string to filter values;
 *   - Load users.
 */
export default function useUsersListHelper() {
  const location = useLocation();
  const filterValues = useMemo(() => queryStringToFilterValues(location.search), [location.search]);
  const { paginatedUsers, reloadUsers } = useUsersLoader(filterValues);
  const onPaginationOrSortChange = useSortAndPagination(filterValues);

  return {
    filterValues,
    onPaginationOrSortChange,
    paginatedUsers,
    reloadUsers,
  };
}

function useUsersLoader(filterValues: FdjUsersFilterValues) {
  const apiParams = useMemo(() => toApiSearchCommand(filterValues), [filterValues]);
  const { data, setData } = useDataLoader<PaginatedListResponse<FdjUser>>('/api/users', apiParams);
  const reloadUsers = useCallback(async () => {
    const response = await ApiHelper.get('/api/users', apiParams);
    setData(response);
  }, [setData, apiParams]);
  return {
    paginatedUsers: data,
    reloadUsers,
  };
}

/**
 * Function to call to update pagination and sort values in the query string.
 */
function useSortAndPagination(filterValues: FdjUsersFilterValues) {
  const navigate = useNavigate();
  return (newCommand: PaginationAndSortCommand<FdjUsersSortableFields>) => {
    const values = mergePaginationAndSortCommandWithParams(filterValues, newCommand);
    const newSearch = toQueryString(values);
    navigate({ search: newSearch }, { replace: false });
  };
}
